import { ALL_FISH, type FishDefinition } from '@/data/fish';
import type { SpriteDefinition } from '@/render/spriteBaker';

/**
 * Fish portraits, drawn from a body archetype plus a colour scheme.
 *
 * Thirty-three hand-authored grids would be thirty-three things to keep in
 * sync and a day of pixel-pushing for art seen a few seconds at a time.
 * Generating from a silhouette and a palette gives every species a shape that
 * actually reads as that fish — a deep-bodied bluegill, a torpedo tuna, a
 * billed swordfish — while staying one function to fix if the style changes.
 *
 * Each sprite carries its own palette, so per-species colour costs nothing.
 */

export const FISH_SPRITE_WIDTH = 40;
export const FISH_SPRITE_HEIGHT = 20;

/** Body silhouettes. `profile` is half-height as a fraction of the maximum. */
export type BodyShape = 'slim' | 'panfish' | 'torpedo' | 'flat' | 'eel' | 'bill';

interface Archetype {
  /** Peak half-height in pixels. */
  readonly depth: number;
  /** Where along the body the peak sits, 0..1. */
  readonly peak: number;
  /** Half-height at the tail root, as a fraction of depth. */
  readonly caudal: number;
  /** Length of the tail fin in pixels. */
  readonly tail: number;
  /** A forward spike, for billfish. */
  readonly snout: number;
}

const ARCHETYPES: Readonly<Record<BodyShape, Archetype>> = {
  slim: { depth: 3, peak: 0.42, caudal: 0.3, tail: 5, snout: 0 },
  panfish: { depth: 7, peak: 0.45, caudal: 0.22, tail: 5, snout: 0 },
  torpedo: { depth: 5, peak: 0.38, caudal: 0.22, tail: 6, snout: 0 },
  flat: { depth: 8, peak: 0.5, caudal: 0.35, tail: 4, snout: 0 },
  eel: { depth: 2, peak: 0.5, caudal: 0.7, tail: 4, snout: 0 },
  bill: { depth: 4, peak: 0.34, caudal: 0.18, tail: 7, snout: 8 },
};

export type Pattern = 'plain' | 'stripes' | 'bars' | 'spots' | 'mottled';

export interface FishArt {
  readonly shape: BodyShape;
  /** Back, flank and belly, darkest to lightest. */
  readonly back: string;
  readonly flank: string;
  readonly belly: string;
  readonly fin: string;
  readonly pattern: Pattern;
  /** Colour of the pattern, when it has one. */
  readonly mark?: string;
}

/**
 * Colours follow the real animal where it has a recognisable one — the red
 * flush of a rainbow trout, a koi's white-and-orange, the tuna's steel back.
 */
export const FISH_ART: Readonly<Record<string, FishArt>> = {
  // Pond
  bluegill: { shape: 'panfish', back: '#3f6b52', flank: '#79a86b', belly: '#e8c96a', fin: '#2f5240', pattern: 'bars', mark: '#2f5240' },
  crucianCarp: { shape: 'panfish', back: '#6b5a20', flank: '#b89a4e', belly: '#e6d59a', fin: '#7a6530', pattern: 'plain' },
  pondLoach: { shape: 'eel', back: '#4a3b28', flank: '#7d6544', belly: '#c9b183', fin: '#3a2e1f', pattern: 'mottled', mark: '#3a2e1f' },
  tilapia: { shape: 'panfish', back: '#4e5f6b', flank: '#8fa3ad', belly: '#dfe6e8', fin: '#3a4750', pattern: 'bars', mark: '#3a4750' },
  koi: { shape: 'torpedo', back: '#e0663a', flank: '#f5f0e6', belly: '#ffffff', fin: '#e0663a', pattern: 'spots', mark: '#c8452a' },
  goldenCarp: { shape: 'torpedo', back: '#c8952a', flank: '#f2c66d', belly: '#fbe9b0', fin: '#a8761c', pattern: 'plain' },

  // Lake
  yellowPerch: { shape: 'panfish', back: '#4f6b2a', flank: '#d8b23f', belly: '#f0e0a8', fin: '#c05a2a', pattern: 'bars', mark: '#3d5220' },
  largemouthBass: { shape: 'torpedo', back: '#3f5c3a', flank: '#7fa05f', belly: '#e2e0c0', fin: '#334a30', pattern: 'stripes', mark: '#334a30' },
  lakeTrout: { shape: 'torpedo', back: '#3d4a52', flank: '#7d8f96', belly: '#dfe6e8', fin: '#2f3a40', pattern: 'spots', mark: '#dfe6e8' },
  walleye: { shape: 'torpedo', back: '#5c5a2f', flank: '#b5a95f', belly: '#efe6c0', fin: '#46431f', pattern: 'mottled', mark: '#46431f' },
  northernPike: { shape: 'eel', back: '#3f5230', flank: '#6f8f4a', belly: '#e0e0b0', fin: '#2f3f22', pattern: 'spots', mark: '#e6e6b8' },
  muskellunge: { shape: 'eel', back: '#4a5236', flank: '#8a9159', belly: '#e6e2c2', fin: '#38402a', pattern: 'bars', mark: '#38402a' },

  // River
  minnow: { shape: 'slim', back: '#5a6b7a', flank: '#a9bcc9', belly: '#e8f0f4', fin: '#48586b', pattern: 'plain' },
  chub: { shape: 'slim', back: '#4f5a4a', flank: '#9aa88f', belly: '#e4e8d8', fin: '#3d463a', pattern: 'plain' },
  rainbowTrout: { shape: 'torpedo', back: '#4a6b6b', flank: '#c9a0a8', belly: '#f0e8e0', fin: '#3a5252', pattern: 'spots', mark: '#3a5252' },
  brownTrout: { shape: 'torpedo', back: '#5c4a2a', flank: '#c9a35f', belly: '#efe0b8', fin: '#46381f', pattern: 'spots', mark: '#c8452a' },
  grayling: { shape: 'torpedo', back: '#4a5a72', flank: '#9fb0c4', belly: '#e6ecf2', fin: '#6a5a8f', pattern: 'spots', mark: '#3a4658' },
  smallmouthBass: { shape: 'torpedo', back: '#5a4f2f', flank: '#a8925a', belly: '#e6dcbc', fin: '#453c22', pattern: 'bars', mark: '#453c22' },
  catfish: { shape: 'eel', back: '#3f3a30', flank: '#6f6650', belly: '#d8cdb0', fin: '#302c24', pattern: 'plain' },
  salmon: { shape: 'torpedo', back: '#4a5a6b', flank: '#d08a72', belly: '#f2e2d8', fin: '#3a4652', pattern: 'spots', mark: '#3a4652' },
  sturgeon: { shape: 'eel', back: '#4a4a42', flank: '#8a8a78', belly: '#ded8c0', fin: '#38382f', pattern: 'bars', mark: '#38382f' },

  // Ocean
  sardine: { shape: 'slim', back: '#3f6b7a', flank: '#bcccd4', belly: '#f0f6f8', fin: '#31555f', pattern: 'plain' },
  anchovy: { shape: 'slim', back: '#4a7a8a', flank: '#c4d8e0', belly: '#f2f8fa', fin: '#3a606c', pattern: 'stripes', mark: '#8fb0bc' },
  herring: { shape: 'slim', back: '#456b82', flank: '#c0d0d8', belly: '#f2f6f8', fin: '#35525f', pattern: 'plain' },
  mackerel: { shape: 'torpedo', back: '#2f5266', flank: '#9fc0cc', belly: '#eef4f6', fin: '#25404f', pattern: 'stripes', mark: '#1f3a4a' },
  flounder: { shape: 'flat', back: '#5a4f3a', flank: '#a3937a', belly: '#e2dcc8', fin: '#453d2c', pattern: 'spots', mark: '#453d2c' },
  cod: { shape: 'torpedo', back: '#5a5a42', flank: '#a8a385', belly: '#e6e2cc', fin: '#454431', pattern: 'mottled', mark: '#454431' },
  seaBass: { shape: 'torpedo', back: '#3a4a5a', flank: '#9aa8b5', belly: '#e6ecf0', fin: '#2c3945', pattern: 'plain' },
  redSnapper: { shape: 'panfish', back: '#a8342a', flank: '#d85f4a', belly: '#f2c0b0', fin: '#8a2620', pattern: 'plain' },
  halibut: { shape: 'flat', back: '#3f4a42', flank: '#7d8a80', belly: '#e2e6e0', fin: '#2f3a33', pattern: 'mottled', mark: '#2f3a33' },
  mahiMahi: { shape: 'torpedo', back: '#2f8a6b', flank: '#7fd0a8', belly: '#f2e07a', fin: '#1f6b52', pattern: 'spots', mark: '#1f6b52' },
  tuna: { shape: 'torpedo', back: '#2a3f5a', flank: '#8fa3b5', belly: '#e6ecf2', fin: '#d8b23f', pattern: 'plain' },
  swordfish: { shape: 'bill', back: '#2f3a52', flank: '#8a97a8', belly: '#e2e8ee', fin: '#242d3f', pattern: 'plain' },

  // Elsewhere
  moonfish: { shape: 'panfish', back: '#5a4a8a', flank: '#a897d8', belly: '#e8e2f8', fin: '#443a6b', pattern: 'spots', mark: '#f2f0ff' },
};

/** Palette characters. Fixed, because the generator writes them. */
const CHARS = { back: 'k', flank: 'm', belly: 'b', fin: 'f', mark: 'p', eye: 'e', glint: 'w' };

/**
 * Draw one fish.
 *
 * Built as a mutable grid and flattened at the end, which is far clearer for
 * overlapping parts (fins over body, pattern over flank, eye over everything)
 * than trying to decide each pixel in one pass.
 */
export function drawFish(art: FishArt): { rows: string[]; palette: Record<string, string> } {
  const shape = ARCHETYPES[art.shape];
  const grid: string[][] = Array.from({ length: FISH_SPRITE_HEIGHT }, () =>
    Array.from({ length: FISH_SPRITE_WIDTH }, () => '.'),
  );

  const centreY = Math.floor(FISH_SPRITE_HEIGHT / 2);
  const noseX = 2 + shape.snout;
  const tailRootX = FISH_SPRITE_WIDTH - 3 - shape.tail;
  const bodyLength = tailRootX - noseX;

  // --- Body -------------------------------------------------------------
  for (let x = noseX; x <= tailRootX; x++) {
    const t = (x - noseX) / bodyLength;
    // Rounded at the head, tapering to the tail root.
    const rise = Math.sin(Math.min(1, t / shape.peak) * (Math.PI / 2));
    const fall = 1 - (t - shape.peak) / (1 - shape.peak);
    const factor = t <= shape.peak ? rise : shape.caudal + (1 - shape.caudal) * Math.max(0, fall);
    const half = Math.max(1, Math.round(shape.depth * factor));

    for (let dy = -half; dy <= half; dy++) {
      const y = centreY + dy;
      if (y < 0 || y >= FISH_SPRITE_HEIGHT) continue;
      const depth = dy / half;
      grid[y]![x] = depth < -0.35 ? CHARS.back : depth > 0.45 ? CHARS.belly : CHARS.flank;
    }
  }

  // --- Tail ---------------------------------------------------------------
  const caudalRoot = Math.max(1, Math.round(shape.depth * shape.caudal));
  for (let i = 0; i <= shape.tail; i++) {
    const x = tailRootX + i;
    if (x >= FISH_SPRITE_WIDTH) break;
    // Grows from the actual body depth at the root, so the tail joins the fish
    // rather than floating behind it as a separate wedge.
    const t = i / shape.tail;
    const spread = Math.max(1, Math.round(caudalRoot + (shape.depth * 0.55) * t));
    for (let dy = -spread; dy <= spread; dy++) {
      // Forked, but only near the tip and only a little, so the two lobes stay
      // visibly one fin.
      if (t > 0.65 && Math.abs(dy) < spread * 0.3) continue;
      const y = centreY + dy;
      if (y >= 0 && y < FISH_SPRITE_HEIGHT) grid[y]![x] = CHARS.fin;
    }
  }

  // --- Fins ---------------------------------------------------------------
  const dorsalFrom = Math.round(noseX + bodyLength * 0.3);
  const dorsalTo = Math.round(noseX + bodyLength * 0.62);
  for (let x = dorsalFrom; x <= dorsalTo; x++) {
    const t = (x - dorsalFrom) / Math.max(1, dorsalTo - dorsalFrom);
    const height = Math.max(1, Math.round(1 + Math.sin(t * Math.PI) * (shape.depth * 0.35)));
    // Started from the body edge at this column, so the fin sits on the fish
    // instead of hovering above the deepest point of it.
    const bodyTop = topOfBody(grid, x, centreY);
    for (let i = 1; i <= height; i++) {
      const y = bodyTop - i;
      if (y >= 0) grid[y]![x] = CHARS.fin;
    }
  }

  const pectoralX = Math.round(noseX + bodyLength * 0.28);
  for (let i = 0; i < 4; i++) {
    const y = centreY + Math.round(shape.depth * 0.35) + i;
    const x = pectoralX + i;
    if (y < FISH_SPRITE_HEIGHT && x < FISH_SPRITE_WIDTH) grid[y]![x] = CHARS.fin;
  }

  // --- Bill ---------------------------------------------------------------
  for (let i = 0; i < shape.snout; i++) {
    grid[centreY]![2 + i] = CHARS.fin;
  }

  // --- Pattern ------------------------------------------------------------
  applyPattern(grid, art, noseX, tailRootX, centreY, shape.depth);

  // --- Eye ----------------------------------------------------------------
  const eyeX = Math.round(noseX + bodyLength * 0.12);
  const eyeY = centreY - Math.max(1, Math.round(shape.depth * 0.3));
  if (grid[eyeY]?.[eyeX] && grid[eyeY][eyeX] !== '.') {
    grid[eyeY][eyeX] = CHARS.eye;
    if (grid[eyeY - 1]?.[eyeX] && grid[eyeY - 1]![eyeX] !== '.') grid[eyeY - 1]![eyeX] = CHARS.glint;
  }

  const palette: Record<string, string> = {
    [CHARS.back]: art.back,
    [CHARS.flank]: art.flank,
    [CHARS.belly]: art.belly,
    [CHARS.fin]: art.fin,
    [CHARS.mark]: art.mark ?? art.back,
    [CHARS.eye]: '#141820',
    [CHARS.glint]: '#f4f8fa',
  };

  return { rows: grid.map((row) => row.join('')), palette };
}

/** Highest row of drawn body in a column, so fins can sit against it. */
function topOfBody(grid: string[][], x: number, fallbackY: number): number {
  for (let y = 0; y < FISH_SPRITE_HEIGHT; y++) {
    if (grid[y]?.[x] && grid[y]![x] !== '.') return y;
  }
  return fallbackY;
}

function applyPattern(
  grid: string[][],
  art: FishArt,
  noseX: number,
  tailRootX: number,
  centreY: number,
  depth: number,
): void {
  if (art.pattern === 'plain') return;

  const onBody = (x: number, y: number): boolean => {
    const cell = grid[y]?.[x];
    return cell === CHARS.flank || cell === CHARS.back;
  };

  switch (art.pattern) {
    case 'bars':
      for (let x = noseX + 4; x < tailRootX; x += 5) {
        for (let y = centreY - depth; y <= centreY + depth; y++) {
          if (onBody(x, y)) grid[y]![x] = CHARS.mark;
        }
      }
      break;

    case 'stripes':
      for (let x = noseX; x < tailRootX; x++) {
        const y = centreY - Math.round(depth * 0.35);
        if (onBody(x, y)) grid[y]![x] = CHARS.mark;
      }
      break;

    case 'spots':
      // Deterministic scatter: the same species always looks the same.
      for (let x = noseX + 2; x < tailRootX; x += 3) {
        for (let y = centreY - depth; y <= centreY; y += 3) {
          const jitter = (x * 7 + y * 13) % 5;
          if (jitter < 2 && onBody(x, y)) grid[y]![x] = CHARS.mark;
        }
      }
      break;

    case 'mottled':
      for (let x = noseX; x < tailRootX; x++) {
        for (let y = centreY - depth; y <= centreY + depth; y++) {
          if ((x * 5 + y * 11) % 7 === 0 && onBody(x, y)) grid[y]![x] = CHARS.mark;
        }
      }
      break;

    default:
      break;
  }
}

/** Sprite definitions for every species, keyed by fish id. */
export const FISH_SPRITES: Readonly<Record<string, SpriteDefinition>> = Object.fromEntries(
  ALL_FISH.map((fish: FishDefinition) => {
    const art = FISH_ART[fish.id];
    if (!art) throw new Error(`Fish "${fish.id}" has no art`);
    const { rows, palette } = drawFish(art);
    return [fish.id, { name: `fish-${fish.id}`, rows, palette }];
  }),
);
